// The two data screens, as the wire delivers them.
//
// Types are the BACKEND's field names, snake_case and all. Renaming them on the
// way in would put a translation layer between the response and the screen, and
// the first time a field is added on one side and forgotten on the other the
// symptom is `undefined` rendered as a blank cell rather than a type error. The
// same reason `PortalIdentity` in auth/api.ts keeps the wire's shape.
//
// Quantities are STRINGS, not numbers, and that is deliberate on the service's
// side: a quantity the ledger holds to six decimal places is not always
// representable as an IEEE-754 double, and a customer reconciling an invoice
// against this page is exactly the person who would notice `29204.1` coming
// back as `29204.099999999999`. So a quantity is displayed as it arrived, and
// the one place it is parsed — chart geometry, where the value becomes a pixel
// height — says so at the call site.
//
// Moments are ISO-8601 with an offset. "26 minutes ago" is a fact about when
// the page was looked at, so it is computed here and not on the server.

import { API_BASE, jsonOrRefusal, type AuthorizedFetch } from '../auth/api';

/** A half-open window, `start` inclusive and `end` exclusive. */
export interface Period {
  start: string;
  end: string;
}

/**
 * The retirement on a customer or a deployment: when, why, who.
 *
 * Carried on every row that can be retired, as `retired: Retired | null`.
 * `null` is a row we still serve. Retirement is a mark and not a deletion —
 * the rows are kept as the record of what happened — and every list hides a
 * former row (retired, or on a customer list superseded) by default and brings
 * it back under "Show former customers" / "Show former instances", with the
 * list's `former_hidden` saying how many the default hid. The three fields are
 * the service's own, raw: the badge is worded on this side.
 */
export interface Retired {
  at: string;
  reason: string;
  by: string;
}

/** The query that asks a list for its former rows, or nothing for the default. */
export function formerQuery(showFormer: boolean): string {
  return showFormer ? 'show_former=true' : '';
}

/** One dimension's month: what was recorded, and what a channel settled. */
export interface MeteringDimension {
  dimension: string;
  hours_recorded: number;
  instances_reporting: number;
  quantity_recorded: string | null;
  /**
   * What a sales channel holds for this dimension, or null where no channel
   * has answered for any of it yet. Null is an absence, not a zero — a
   * settled zero would say the channel had accepted nothing to bill, which is
   * a different fact from nobody having replied.
   */
  quantity_settled: string | null;
  /**
   * What was packaged and deliberately not sent, because the agreement is
   * withheld. Null where nothing was. Never part of the settled figure, and
   * taken out of the recorded side before the carry is worked out.
   */
  quantity_withheld: string | null;
  /** Recorded minus withheld minus settled: whole-unit flooring, and hours still in flight. */
  carry: string | null;
  latest_usage_hour: string | null;
}

/** One submission handed to a channel, and what came back. */
export interface MeteringRecord {
  public_id: string;
  usage_hour: string | null;
  instance: string;
  dimension: string;
  quantity_recorded: string | null;
  quantity_submitted: string | null;
  /** The core's own word. Empty means the channel has not answered yet. */
  outcome: string;
  channel: string;
  channel_record_ref: string;
  submitted_at: string | null;
  resolved_at: string | null;
}

/** One instance's bar in every month of the chart, in axis order. */
export interface UsageSeries {
  instance: string;
  quantities: (string | null)[];
}

/**
 * One dimension's chart: a series per instance, over the shared axis.
 *
 * Dimensions are separate charts rather than layers of one, because they are
 * not summable — one measures volume, another measures well-days, and a stack
 * that added them would draw a total in no unit at all.
 */
export interface UsageDimensionChart {
  dimension: string;
  series: UsageSeries[];
}

export interface UsageByMonth {
  /** Month starts, ISO-8601, oldest first. The axis, shared by every chart. */
  months: string[];
  dimensions: UsageDimensionChart[];
}

export interface MeteringSummary {
  dimensions_reporting: number;
  hours_recorded_this_month: number;
  records_this_month: number;
  records_resolved_this_month: number;
  /** Not windowed: an unanswered hour from last month is still outstanding. */
  records_awaiting_answer: number;
  latest_submission_at: string | null;
}

export interface MeteringScreen {
  period: Period;
  /**
   * The months this screen will answer for, oldest first, as `YYYY-MM`.
   *
   * Sent rather than inferred from the chart's axis: a client deriving the
   * list itself would break silently the day the two stop being the same
   * window.
   */
  selectable_periods: string[];
  dimensions: MeteringDimension[];
  records: MeteringRecord[];
  usage_by_month: UsageByMonth;
  summary: MeteringSummary;
}

/** A subscription, as both fleet lists carry it. */
export interface FleetSubscription {
  public_id: string;
  channel: string;
  agreement_ref: string;
  state: string;
  started_at: string | null;
  ended_at: string | null;
}

export interface InstanceUsage {
  dimension: string;
  quantity_recorded: string | null;
  latest_usage_hour: string | null;
}

/**
 * One instance, and the agreement it runs under.
 *
 * No region and no version: the model records neither, and the endpoint says
 * why. A deployment's region is on the request that ASKED for it, which is why
 * it appears on `ProvisioningRow` instead.
 */
export interface FleetInstance {
  public_id: string;
  name: string;
  client_id: string;
  state: string;
  /**
   * Where the customer reaches this deployment, or empty.
   *
   * Empty is a real state and not a gap: an instance can be RUNNING before
   * anybody has recorded where it answers. Empty string rather than null
   * because that is what the model stores — a blank CharField — and this
   * type says what the wire says.
   */
  access_url: string;
  created_at: string | null;
  latest_usage_hour: string | null;
  retired: Retired | null;
  subscription: FleetSubscription;
  usage: InstanceUsage[];
}

/** A deployment this customer asked for, and where the ask stands. */
export interface ProvisioningRow {
  public_id: string;
  region: string;
  instance_name: string;
  decision_pack: string;
  state: string;
  note: string;
  requested_at: string | null;
}

export interface FleetScreen {
  period: Period;
  /**
   * The months this screen will answer for, oldest first, as `YYYY-MM`.
   *
   * The same list Metering sends, from the same server-side helper, because
   * two screens that disagree about how far back they go are two screens a
   * customer cannot reconcile against each other.
   */
  selectable_periods: string[];
  /**
   * How many retired instances the default left out of `instances`. An
   * instance's only way of being former is retirement; the key is named as
   * every list names it.
   */
  former_hidden: number;
  instances: FleetInstance[];
  subscriptions: FleetSubscription[];
  provisioning_requests: ProvisioningRow[];
}

/**
 * Read the metering screen.
 *
 * A module-level function rather than a closure built in a component, so that
 * `usePortalRead` can hold it in a dependency array without re-running the
 * request on every render.
 */
export function readMetering(authorizedFetch: AuthorizedFetch): Promise<MeteringScreen> {
  return read<MeteringScreen>(authorizedFetch, 'metering');
}

/**
 * Read the metering screen for one month, as `YYYY-MM`.
 *
 * Separate from `readMetering` rather than an optional argument, because
 * `usePortalRead` holds its read in a dependency array and needs it stable
 * across renders. A caller wanting a specific month memoises this per month;
 * a caller wanting the current one keeps using the module-level function and
 * needs no memoisation at all.
 */
export function readMeteringFor(
  authorizedFetch: AuthorizedFetch,
  period: string,
): Promise<MeteringScreen> {
  return read<MeteringScreen>(
    authorizedFetch,
    `metering/?period=${encodeURIComponent(period)}`,
  );
}

/**
 * Read the fleet screen. Module-level for the same reason.
 *
 * `showFormer` brings back the instances the default hides; a page that
 * offers the toggle memoises the call per setting, as it does per month.
 */
export function readFleet(
  authorizedFetch: AuthorizedFetch,
  showFormer = false,
): Promise<FleetScreen> {
  const query = formerQuery(showFormer);
  return read<FleetScreen>(authorizedFetch, query ? `fleet/?${query}` : 'fleet');
}

/** Read the fleet screen for one month, as `YYYY-MM`. Separate from
 * `readFleet` for the reason `readMeteringFor` is separate from
 * `readMetering`. */
export function readFleetFor(
  authorizedFetch: AuthorizedFetch,
  period: string,
  showFormer = false,
): Promise<FleetScreen> {
  const former = formerQuery(showFormer);
  return read<FleetScreen>(
    authorizedFetch,
    `fleet/?period=${encodeURIComponent(period)}${former ? `&${former}` : ''}`,
  );
}

async function read<T>(authorizedFetch: AuthorizedFetch, screen: string): Promise<T> {
  // A screen may carry its own query string, in which case it has already
  // written the trailing slash the service wants.
  const path = screen.includes('?') ? screen : `${screen}/`;
  const response = await authorizedFetch(`${API_BASE}/${path}`, { method: 'GET' });
  return jsonOrRefusal<T>(response);
}
